/**
 * <zg-product-gallery>: tabs and variant updates for blocks/_zg-product-gallery.liquid.
 *
 * The block renders every panel and tab, the first one selected, so the first image shows without
 * JS. This element adds
 * - the tabs (WAI-ARIA tabs pattern, automatic activation): click, Left/Right (mirrored in RTL),
 *   Home/End, with a roving tabindex;
 * - variant changes: Horizon's variant picker fetches the product page for the new selection and
 *   dispatches ProductSelectEvent; when the latest selection's promise resolves, this element
 *   morphs itself from the same block in that page (the server picks the new variant's images) and
 *   re-selects the tab the shopper was on (same Gallery image block, else same label, else same
 *   position); responses of superseded selections are ignored;
 * - in the theme editor, selecting a Gallery image block shows its panel.
 */
import { StandardEvents } from '@shopify/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';

/**
 * @typedef {object} TabKey
 * @property {string} shotId - The Gallery image block id.
 * @property {string} label - The tab's visible label.
 * @property {number} index - The tab's position.
 */

class ZgProductGallery extends HTMLElement {
  /** @type {AbortController | null} */
  #abort = null;
  #warmed = false;
  /** Counts variant selections; only the latest one's response may morph (see #onProductSelect). */
  #generation = 0;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('keydown', this.#onKeydown, { signal });
    this.addEventListener('pointerover', this.#warm, { signal });
    this.addEventListener('focusin', this.#warm, { signal });

    this.closest('.shopify-section, dialog')?.addEventListener(StandardEvents.productSelect, this.#onProductSelect, {
      signal,
    });

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:block:select', this.#onBlockSelect, { signal });
    }
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
  }

  /** @returns {HTMLButtonElement[]} */
  get tabs() {
    return Array.from(this.querySelectorAll('.zg-product-gallery__tabs [role="tab"]'));
  }

  /**
   * Shows a tab's panel and makes it the tab in the tab order.
   * @param {HTMLButtonElement} tab
   * @param {{ focus?: boolean }} [options]
   */
  select(tab, { focus = false } = {}) {
    for (const candidate of this.tabs) {
      const selected = candidate === tab;
      candidate.setAttribute('aria-selected', String(selected));
      candidate.tabIndex = selected ? 0 : -1;

      const panelId = candidate.getAttribute('aria-controls');
      const panel = panelId ? this.querySelector(`#${CSS.escape(panelId)}`) : null;
      if (panel instanceof HTMLElement) panel.hidden = !selected;
    }

    if (focus) tab.focus();
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const tab = event.target instanceof Element ? event.target.closest('[role="tab"]') : null;
    if (!(tab instanceof HTMLButtonElement) || !this.contains(tab)) return;

    this.select(tab);
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    const tab = event.target instanceof Element ? event.target.closest('[role="tab"]') : null;
    if (!(tab instanceof HTMLButtonElement) || event.altKey || event.ctrlKey || event.metaKey) return;

    const tabs = this.tabs;
    const index = tabs.indexOf(tab);
    if (index === -1) return;

    const rtl = getComputedStyle(this).direction === 'rtl';
    const step = { ArrowRight: rtl ? -1 : 1, ArrowLeft: rtl ? 1 : -1 }[event.key];

    let next;
    if (step) next = tabs[(index + step + tabs.length) % tabs.length];
    else if (event.key === 'Home') next = tabs[0];
    else if (event.key === 'End') next = tabs[tabs.length - 1];
    if (!next) return;

    event.preventDefault();
    this.select(next, { focus: true });
  };

  /**
   * Once the shopper reaches for the tabs, load the other panels' lazy images so they show at once.
   */
  #warm = () => {
    if (this.#warmed) return;
    this.#warmed = true;

    for (const image of this.querySelectorAll('.zg-product-gallery__panel[hidden] img[loading="lazy"]')) {
      image.setAttribute('loading', 'eager');
    }
  };

  /** @param {Event} event */
  #onBlockSelect = (event) => {
    const blockId = /** @type {CustomEvent<{ blockId?: string }>} */ (event).detail?.blockId;
    if (!blockId) return;

    const tab = this.tabs.find((candidate) => candidate.dataset.shotId === blockId);
    if (tab) this.select(tab);
  };

  /** @param {Event} event */
  #onProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const { promise } = /** @type {Event & { promise?: Promise<any> }} */ (event);
    if (!promise) return;

    // A newer selection can start before this one's listeners run (the variant snap in
    // zg-product-information.js re-picks inside the same resolution), so a superseded response must
    // not morph: it would show the wrong variant's images and lose the shopper's tab.
    const generation = ++this.#generation;

    promise
      .then(({ detail }) => {
        if (!detail?.html || !this.isConnected) return;

        if (detail.newProduct) {
          this.dataset.productId = String(detail.newProduct.id);
        } else if (detail.productId && String(detail.productId) !== this.dataset.productId) {
          return;
        }

        // Product bookkeeping above still applies to a superseded response; the morph doesn't.
        if (generation !== this.#generation) return;

        const blockId = CSS.escape(this.dataset.blockId ?? '');
        const fresh = detail.html.querySelector(`zg-product-gallery[data-block-id="${blockId}"]`);
        if (!fresh) return;

        // Read the selection now, not when the event fired: the shopper may have changed tabs meanwhile.
        const key = this.#selectedKey();
        const active = document.activeElement;
        const tabHadFocus = active instanceof HTMLElement && active.getAttribute('role') === 'tab' && this.contains(active);

        morph(this, fresh, MORPH_OPTIONS);

        this.#warmed = false;
        this.#restore(key, tabHadFocus);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[zg-product-gallery] Variant update failed:', error);
      });
  };

  /** @returns {TabKey | null} */
  #selectedKey() {
    const tabs = this.tabs;
    const index = tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
    const tab = tabs[index];
    if (!tab) return null;

    return {
      shotId: tab.dataset.shotId ?? '',
      label: tab.querySelector('.zg-product-gallery__tab-label')?.textContent?.trim() ?? '',
      index,
    };
  }

  /**
   * Selects the tab matching the one selected before a morph, and keeps keyboard focus on it.
   * @param {TabKey | null} key
   * @param {boolean} tabHadFocus
   */
  #restore(key, tabHadFocus) {
    if (!key) return;

    const tabs = this.tabs;
    const labelOf = (/** @type {HTMLElement} */ tab) =>
      tab.querySelector('.zg-product-gallery__tab-label')?.textContent?.trim() ?? '';
    const tab =
      tabs.find((candidate) => key.shotId && candidate.dataset.shotId === key.shotId) ??
      tabs.find((candidate) => key.label && labelOf(candidate) === key.label) ??
      tabs[key.index];

    if (!tab) return;

    this.select(tab, { focus: tabHadFocus && document.activeElement !== tab });
  }
}

if (!customElements.get('zg-product-gallery')) {
  customElements.define('zg-product-gallery', ZgProductGallery);
}
